import { isEqualWith } from 'lodash-es';

export interface ObjectProps {
  [key: string]: unknown;
}

// https://eslint.org/docs/rules/no-prototype-builtins

export const hasOwnProperty = (
  obj: ObjectProps,
  key: string | number,
): boolean => Object.prototype.hasOwnProperty.call(obj, key);

/**
 * Checks for property existence on an object with a type guard.  It can't infer
 * the types though so you need to provide them:
 *
 * hasOwnPropertyGuard<typeof obj, typeof key, typeof obj[key]>(obj, key);
 *
 * @param obj
 * @param key
 * @returns
 */
export const hasOwnPropertyGuard = <
  T extends ObjectProps,
  K extends keyof any,
  V,
>(
  obj: T,
  key: K,
): obj is T & Record<K, V> => Object.prototype.hasOwnProperty.call(obj, key);

/**
 * Creates a new object with a mix of enumerable properties
 * of the supplied object args.
 *
 * Unlike with object.assign or object spread operators - any defined
 * accessor getter and setters functions are copied across as well,
 * as opposed to being called during the merge and the return value
 * being copied across instead.
 *
 * @param args - the objects to be mixed.
 *
 * If the the last argument is a function it will not be used as a source object.
 * You can use this to transform property keys.
 *
 * It will be called, passing each property key to be copied and the
 * original source object from which it came.  The return value will
 * be used in place of the original property key.
 * @returns - the mixed object.
 */
export const mix = <D>(
  ...args: Array<
    | Record<string, D>
    | ((prop: string, sources: Record<string, D>) => PropertyKey)
  >
) => {
  const sources = args;
  let propTransformer:
    | null
    | ((prop: string, sources: Record<string, D>) => PropertyKey) = null;
  const lastArg = args[args.length - 1];
  if (typeof lastArg === 'function') {
    propTransformer = lastArg;
  }
  const result: Record<string, D> = {};
  sources.forEach((source) => {
    if (source !== propTransformer && typeof source !== 'function') {
      const props = Object.keys(source);
      props.forEach((prop) => {
        const descriptor = Object.getOwnPropertyDescriptor(source, prop) || '';
        const newProp =
          typeof propTransformer === 'function'
            ? propTransformer(prop, source)
            : prop;
        Object.defineProperty(result, newProp, descriptor);
      });
    }
  });

  return result;
};

export const shallowEqual = <A, B>(objA?: A, objB?: B): boolean =>
  isEqualWith(objA, objB, (aval, bval, index) =>
    index === undefined ? undefined : Object.is(aval, bval),
  );

export const getId = (initialId = -1) => {
  let currentId = initialId;
  const counter = () => {
    currentId += 1;
    return currentId;
  };
  counter.getCurrent = () => currentId;
  counter.resetCounter = () => (currentId = initialId);
  return counter;
};

type ComparisonResult = {
  equal: boolean;
  keys: (string | Record<string, string>)[];
};

const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;

  if (
    typeof a !== 'object' ||
    a === null ||
    typeof b !== 'object' ||
    b === null
  ) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return !keysA.some(
    (key) => !keysB.includes(key) || !deepEqual(a[key], b[key]),
  );
};

const objectsDifferingKeys = (
  objA: Record<string, any>,
  objB: Record<string, any>,
  prefix?: string,
): (string | Record<string, string>)[] => {
  const allKeys = new Set([...Object.keys(objA), ...Object.keys(objB)]);
  const differingKeys: (string | Record<string, string>)[] = [];

  Array.from(allKeys).forEach((key) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (
      typeof objA[key] === 'object' &&
      objA[key] !== null &&
      typeof objB[key] === 'object' &&
      objB[key] !== null
    ) {
      const nestedDifferences = objectsDifferingKeys(
        objA[key],
        objB[key],
        fullKey,
      );
      differingKeys.push(...nestedDifferences);
    } else if (!deepEqual(objA[key], objB[key])) {
      if (prefix) {
        const nestedKeyObj: Record<string, string> = {};
        nestedKeyObj[prefix] = key;
        differingKeys.push(nestedKeyObj);
      } else {
        differingKeys.push(key);
      }
    }
  });

  return differingKeys;
};

export const diffObjects = (
  objA: Record<string, any>,
  objB: Record<string, any>,
): ComparisonResult => {
  const differingKeys = objectsDifferingKeys(objA, objB);
  return {
    equal: differingKeys.length === 0,
    keys: differingKeys,
  };
};
